import { useEffect, useState, type ReactNode } from 'react';
import { COLORS } from '../lib/theme';
import { useHasNavDrawer } from '../lib/useNavDrawer';
import { Sidebar, type NavSection, type SidebarLabels } from './Sidebar';
import { TopBar, type TopBarAriaLabels, type TopBarProps } from './TopBar';

/**
 * Everything on the bar the shell may set directly. What is left out is either
 * owned by this component (the drawer toggle, the rail and the routing it
 * needs) or has its own named prop below (logo, labels, the right-hand slot).
 */
export type AppShellTopBarProps = Omit<
  TopBarProps,
  | 'onSidebarToggle'
  | 'slotRight'
  | 'logo'
  | 'logoDark'
  | 'logoAlt'
  | 'ariaLabels'
  | 'navSections'
  | 'currentPath'
  | 'onSignOut'
  | 'onNavigate'
  | 'onNavigateTo'
  | 'showSearch'
  | 'showCart'
>;

export interface AppShellProps {
  children: ReactNode;
  currentPath: string;
  navSections?: NavSection[];
  onSignOut?: () => void;
  logo?: string;
  /** Dark-theme logo variant, forwarded to TopBar. See TopBarProps.logoDark. */
  logoDark?: string;
  logoAlt?: string;
  /** Sidebar labels + bottom slot, forwarded as-is. */
  sidebarLabels?: SidebarLabels;
  sidebarSlotBottom?: ReactNode;
  /** TopBar ARIA labels, forwarded as-is. */
  topBarAriaLabels?: TopBarAriaLabels;
  /** Show the TopBar search icon (full bar only). Defaults to true. */
  topBarShowSearch?: boolean;
  /** Show the TopBar cart icon (full bar only). Defaults to true. */
  topBarShowCart?: boolean;
  /**
   * The rest of the bar's configuration — `variant: 'full'` turns on the
   * wehunt.app pill with its search, cart, locale and combined mobile menu;
   * the defaults keep the minimal wordmark bar viltrapport ships.
   */
  topBar?: AppShellTopBarProps;
  /** Called with the href when a Sidebar nav item is clicked. */
  onSidebarNavigate?: (href: string) => void;
  /** Optional node rendered in the right-side group of the TopBar. */
  topBarSlotRight?: ReactNode;
  /** Optional banner rendered full-width between the TopBar and the content area. */
  banner?: ReactNode;
}

export function AppShell({
  children,
  currentPath,
  navSections,
  onSignOut,
  logo,
  logoDark,
  logoAlt,
  sidebarLabels,
  sidebarSlotBottom,
  topBarAriaLabels,
  topBarShowSearch = true,
  topBarShowCart = true,
  topBar,
  onSidebarNavigate,
  topBarSlotRight,
  banner,
}: AppShellProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const hasDrawer = useHasNavDrawer();

  // `sidebarOpen` is only meaningful while the rail is a drawer. Left set as the
  // viewport grows past the breakpoint it survives out of sight — the rail goes
  // inline and the handle disappears, so nothing can clear it — and then springs
  // the drawer open over the content the moment the viewport narrows again. On an
  // iPad that is a plain rotation away: portrait and landscape sit either side of
  // the breakpoint, so open-rotate-rotate lands you back on a page behind a
  // dimming overlay you never asked for.
  useEffect(() => {
    if (!hasDrawer) setSidebarOpen(false);
  }, [hasDrawer]);

  return (
    <div
      className="wh-topo-bg"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: COLORS.surface,
        color: COLORS.onSurface,
      }}
    >
      {/* The rail goes to the bar as well as to the Sidebar below. In the full
          bar, wherever the Sidebar is off-canvas the bar folds it into its own
          menu and drops the handle, so the drawer is simply never opened there —
          see `indexMode` in TopBar. Above that width, and in the minimal bar,
          the Sidebar on the left is the rail. */}
      <TopBar
        showCta={false}
        {...topBar}
        onSidebarToggle={() => setSidebarOpen((o) => !o)}
        showSearch={topBarShowSearch}
        showCart={topBarShowCart}
        logo={logo}
        logoDark={logoDark}
        logoAlt={logoAlt}
        ariaLabels={topBarAriaLabels}
        slotRight={topBarSlotRight}
        navSections={navSections}
        currentPath={currentPath}
        onSignOut={onSignOut}
        onNavigate={onSidebarNavigate}
        onNavigateTo={onSidebarNavigate}
      />
      {banner && (
        <div
          style={{
            maxWidth: 1440,
            width: '100%',
            margin: '0 auto',
            padding: '0 24px',
            boxSizing: 'border-box',
          }}
        >
          {banner}
        </div>
      )}
      <div
        className="wh-app-inner"
        style={{
          display: 'flex',
          flex: 1,
          alignItems: 'stretch',
          maxWidth: 1440,
          width: '100%',
          margin: '0 auto',
          padding: '24px 24px 32px',
          gap: 24,
        }}
      >
        {sidebarOpen && (
          <div className="wh-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
        )}
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          currentPath={currentPath}
          navSections={navSections}
          onSignOut={onSignOut}
          labels={sidebarLabels}
          slotBottom={sidebarSlotBottom}
          onNavigate={onSidebarNavigate}
        />
        <main style={{ flex: 1, minWidth: 0 }}>{children}</main>
      </div>
    </div>
  );
}
